import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import * as Lucide from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import PushSubscribeBanner from "@/components/PushSubscribeBanner";
import ServiceContactForm from "@/components/ServiceContactForm";
import ServiceMobileCta from "@/components/ServiceMobileCta";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSiteContent, resolveAssetUrl, pickLocalized } from "@/contexts/SiteContentContext";
import { SERVICE_DETAILS, SERVICE_SLUGS } from "@/lib/serviceDetails";
import {
  absoluteUrl,
  buildHreflangAlternates,
  buildServiceJsonLd,
  buildFaqJsonLd,
  OG_LOCALE_MAP,
  SITE_NAME,
} from "@/lib/seo";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  MessageCircle,
  ChevronRight,
} from "lucide-react";

const resolveIcon = (name) => Lucide[name] || Lucide.Sparkles;

const ServiceDetail = () => {
  const { slug } = useParams();
  const { locale, t } = useLanguage();
  const { content } = useSiteContent();
  const data = SERVICE_DETAILS[slug];

  // Find the cover image: prefer admin-overridden image_url from site_content.services, fallback to defaults.
  const liveService = (content?.services || []).find((s) => s.key === slug);
  const cover = resolveAssetUrl(liveService?.image_url || data?.cover || "");

  // Scroll to top on slug change.
  useEffect(() => {
    if (typeof window !== "undefined") window.scrollTo(0, 0);
  }, [slug]);

  const relatedSlugs = useMemo(
    () => SERVICE_SLUGS.filter((s) => s !== slug).slice(0, 3),
    [slug],
  );

  if (!data) {
    return (
      <div className="min-h-screen bg-[#fdfbf7] text-neutral-900">
        <Helmet>
          <title>{`404 · ${SITE_NAME}`}</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <Navigation />
        <main className="pt-40 container-px mx-auto max-w-3xl pb-32">
          <h1 data-testid="service-detail-404" className="font-display text-4xl">404</h1>
          <p className="mt-4 text-neutral-600">
            Diese Leistung gibt es nicht. <Link to="/" className="text-sky-700 underline">Zur Startseite</Link>
          </p>
        </main>
        <Footer />
        <WhatsAppButton />
      </div>
    );
  }

  const Icon = resolveIcon(data.icon);
  const phone = (content?.contact?.whatsapp_number || "+34657138915").replace(/[^+\d]/g, "");
  const waUrl = `https://wa.me/${phone.replace(/^\+/, "")}`;

  const title = pickLocalized(data.title, locale);
  const subtitle = pickLocalized(data.subtitle, locale);
  const eyebrow = pickLocalized(data.eyebrow, locale);
  const intro = pickLocalized(data.intro, locale);
  const benefits = pickLocalized(data.benefits, locale, []) || [];
  const sections = pickLocalized(data.sections, locale, []) || [];
  const faq = pickLocalized(data.faq, locale, []) || [];

  // SEO data
  const metaTitle = pickLocalized(data.metaTitle, locale, title);
  const metaDescription = pickLocalized(data.metaDescription, locale, subtitle);
  const path = `/services/${slug}`;
  const canonicalUrl = absoluteUrl(path);
  const alternates = buildHreflangAlternates(path);
  // Domain-branded OG card always wins; cover image is JSON-LD only.
  const ogImageUrl = absoluteUrl(
    `/api/og.png?domain=${encodeURIComponent(typeof window !== "undefined" ? window.location.hostname : "")}&locale=${locale}`,
  );
  const jsonLdImage = cover && /^https?:\/\//i.test(cover) ? cover : ogImageUrl;
  const ogLocale = OG_LOCALE_MAP[locale] || OG_LOCALE_MAP.de;

  const serviceJsonLd = buildServiceJsonLd({
    name: title,
    description: metaDescription,
    url: canonicalUrl,
    image: jsonLdImage,
  });
  const faqJsonLd = faq.length > 0 ? buildFaqJsonLd(faq) : null;
  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: absoluteUrl("/") },
      { "@type": "ListItem", position: 2, name: "Leistungen", item: `${absoluteUrl("/")}#services` },
      { "@type": "ListItem", position: 3, name: title, item: canonicalUrl },
    ],
  };

  return (
    <div data-testid={`service-detail-${slug}`} className="min-h-screen bg-[#fdfbf7] text-neutral-900">
      <Helmet>
        <html lang={locale} />
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        {alternates.map((a) => (
          <link key={a.hrefLang} rel="alternate" hrefLang={a.hrefLang} href={a.href} />
        ))}
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:locale" content={ogLocale} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={ogImageUrl} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <script type="application/ld+json">{JSON.stringify(serviceJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
        {faqJsonLd && (
          <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        )}
      </Helmet>
      <Navigation />

      <main>
        {/* Hero */}
        <section className="relative pt-32 lg:pt-40 pb-16 lg:pb-24 overflow-hidden">
          {cover && (
            <div className="absolute inset-0 -z-10">
              <img src={cover} alt={title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/30" />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#fdfbf7]" />
            </div>
          )}
          <div className="container-px mx-auto max-w-7xl">
            <Link
              to="/#services"
              data-testid="service-back"
              className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900"
            >
              <ArrowLeft className="w-4 h-4" />
              {t("services.eyebrow")}
            </Link>

            <div className="mt-8 max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/70 backdrop-blur border border-white/60 px-3.5 py-1.5 text-xs uppercase tracking-[0.2em] text-sky-700">
                <Icon className="w-3.5 h-3.5" />
                {eyebrow}
              </div>
              <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-neutral-950 text-balance">
                {title}
              </h1>
              <p className="mt-5 text-lg sm:text-xl text-neutral-700 leading-relaxed max-w-2xl">
                {subtitle}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#service-anfrage"
                  data-testid="service-cta-primary"
                  className="group inline-flex items-center gap-2 rounded-full bg-neutral-950 text-white px-6 py-3.5 text-sm font-medium hover:bg-black transition-colors"
                >
                  Beratung anfragen
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </a>
                <a
                  href={waUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="service-cta-whatsapp"
                  className="inline-flex items-center gap-2 rounded-full bg-white border border-neutral-200 text-neutral-900 px-6 py-3.5 text-sm font-medium hover:shadow-md transition-all"
                >
                  <MessageCircle className="w-4 h-4 text-emerald-600" />
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Intro + Benefits */}
        <section className="container-px mx-auto max-w-7xl pb-16 lg:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-7">
              <div
                className="prose prose-neutral max-w-none
                  prose-p:text-[17px] prose-p:leading-relaxed prose-p:text-neutral-800
                  prose-strong:text-neutral-950
                  prose-a:text-sky-700 prose-a:no-underline hover:prose-a:underline"
                data-testid="service-intro"
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{intro}</ReactMarkdown>
              </div>
            </div>

            <aside
              className="lg:col-span-5 rounded-3xl bg-neutral-950 text-white p-7 lg:p-9 self-start"
              data-testid="service-benefits"
            >
              <div className="text-xs uppercase tracking-[0.22em] text-sky-300 mb-5">
                Highlights
              </div>
              <ul className="space-y-3">
                {benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-[15px] text-neutral-100">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-sky-500/20 grid place-items-center shrink-0">
                      <Check className="w-3 h-3 text-sky-300" />
                    </span>
                    <span className="leading-snug">
                      <ReactMarkdown
                        components={{ p: ({ children }) => <span>{children}</span> }}
                        remarkPlugins={[remarkGfm]}
                      >
                        {b}
                      </ReactMarkdown>
                    </span>
                  </li>
                ))}
              </ul>
            </aside>
          </div>
        </section>

        {/* Sections */}
        {sections.length > 0 && (
          <section className="bg-[#f7f2ea]">
            <div className="container-px mx-auto max-w-7xl py-20 lg:py-28">
              <div className="space-y-12">
                {sections.map((sec, i) => (
                  <article
                    key={i}
                    data-testid={`service-section-${i}`}
                    className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 items-start"
                  >
                    <div className="lg:col-span-4">
                      <div className="text-xs uppercase tracking-[0.22em] text-sky-700 font-medium">
                        0{i + 1}
                      </div>
                      <h2 className="font-display mt-3 text-2xl lg:text-3xl font-medium text-neutral-950 tracking-tight text-balance">
                        {sec.title}
                      </h2>
                    </div>
                    <div className="lg:col-span-8">
                      <div
                        className="prose prose-neutral max-w-none
                          prose-p:text-[17px] prose-p:leading-relaxed prose-p:text-neutral-800
                          prose-li:text-[16px] prose-li:text-neutral-800
                          prose-strong:text-neutral-950
                          prose-headings:font-display"
                      >
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{sec.body}</ReactMarkdown>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FAQ */}
        {faq.length > 0 && (
          <section className="container-px mx-auto max-w-4xl py-20 lg:py-28">
            <div className="text-xs uppercase tracking-[0.22em] text-sky-700 font-medium">FAQ</div>
            <h2 className="font-display mt-3 text-3xl lg:text-5xl font-light tracking-tighter text-neutral-950 text-balance">
              Häufige Fragen
            </h2>
            <div className="mt-8 space-y-3">
              {faq.map((f, i) => (
                <details
                  key={i}
                  data-testid={`service-faq-${i}`}
                  className="group rounded-2xl bg-white border border-neutral-200 px-5 lg:px-6"
                >
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-4 py-4 lg:py-5">
                    <span className="font-medium text-neutral-950 text-[15px] lg:text-base text-balance">
                      {f.q}
                    </span>
                    <ChevronRight className="w-4 h-4 text-neutral-400 group-open:rotate-90 transition-transform shrink-0" />
                  </summary>
                  <div className="pb-5 text-neutral-700 leading-relaxed text-[15px]">
                    {f.a}
                  </div>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* Embedded service contact form */}
        <ServiceContactForm slug={slug} serviceTitle={title} waUrl={waUrl} />

        {/* Related services */}
        <section className="container-px mx-auto max-w-7xl py-16 lg:py-24">
          <div className="text-xs uppercase tracking-[0.22em] text-sky-700 font-medium">
            Weitere Leistungen
          </div>
          <h2 className="font-display mt-3 text-2xl lg:text-4xl font-light tracking-tighter text-neutral-950">
            Das könnte Sie auch interessieren
          </h2>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
            {relatedSlugs.map((s) => {
              const d = SERVICE_DETAILS[s];
              const RIcon = resolveIcon(d.icon);
              return (
                <Link
                  key={s}
                  to={`/services/${s}`}
                  data-testid={`service-related-${s}`}
                  className="group rounded-3xl bg-white border border-neutral-200 p-6 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(0,0,0,0.06)] transition-all"
                >
                  <div className="w-11 h-11 rounded-2xl bg-neutral-950 text-white grid place-items-center">
                    <RIcon className="w-5 h-5" />
                  </div>
                  <div className="mt-5 font-display text-lg font-medium text-neutral-950 leading-snug">
                    {pickLocalized(d.title, locale)}
                  </div>
                  <div className="mt-3 inline-flex items-center text-sm text-sky-700">
                    Mehr erfahren
                    <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppButton />
      <PushSubscribeBanner />
      <ServiceMobileCta waUrl={waUrl} anchorId="service-anfrage" />
    </div>
  );
};

export default ServiceDetail;
